import React, {useEffect, useState, useCallback, useRef} from "react"
import {useParams} from "react-router-dom"

import {CustomerCard} from "./CustomerCard"
import {CustomerError} from "./CustomerError"
import {useCustomerID} from "./useCustomerID"

import {api, Customer} from "@indebted/api"
import {LoadingPage} from "@indebted/components/Loading"
import {Layout} from "@indebted/components/Layout"

type PageState =
	| {status: "loading"}
	| {status: "loaded"; customer: Customer; refreshing: boolean}
	| {status: "error"; message: string}

function CustomerPage() {
	const [state, setState] = useState<PageState>({status: "loading"})
	const id = useCustomerID()
	const {viewerTimezoneOffset = new Date().getTimezoneOffset()} = useParams()
	const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		load()

		return () => {
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current)
				refreshTimerRef.current = null
			}
		}
	}, [id, viewerTimezoneOffset]) // eslint-disable-line react-hooks/exhaustive-deps

	function load() {
		setState({status: "loading"})
		api.customer
			.get(id, viewerTimezoneOffset)
			.then((customer) => {
				setState({status: "loaded", customer, refreshing: false})
			})
			.catch((error) => {
				setState({status: "error", message: error.Message})
			})
	}

	const refresh = useCallback(
		(delayMs?: number) => {
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current)
				refreshTimerRef.current = null
			}

			const doRefresh = () => {
				refreshTimerRef.current = null
				api.customer
					.get(id, viewerTimezoneOffset)
					.then((customer) => {
						setState({status: "loaded", customer, refreshing: false})
					})
					.catch(() => {
						setState((prev) => (prev.status === "loaded" ? {...prev, refreshing: false} : prev))
					})
			}
			if (delayMs) {
				setState((prev) => (prev.status === "loaded" ? {...prev, refreshing: true} : prev))
				refreshTimerRef.current = setTimeout(doRefresh, delayMs)
			} else {
				doRefresh()
			}
		},
		[id, viewerTimezoneOffset],
	)

	if (state.status === "loading") {
		return (
			<Layout>
				<LoadingPage />
			</Layout>
		)
	}

	if (state.status === "error") {
		return (
			<Layout>
				<CustomerError error={state.message} />
			</Layout>
		)
	}

	return (
		<Layout>
			<CustomerCard
				customer={state.customer}
				refreshCustomerData={refresh}
				refreshingCustomerData={state.refreshing}
			/>
		</Layout>
	)
}

export {CustomerPage}
